/**
 * playAviChime — son court, discret et généré (Web Audio API).
 *
 * Deux notes empilées (C6 = 1046.5 Hz, E6 = 1318.5 Hz) avec enveloppe
 * exponentielle 20 ms attaque / 220 ms release. Aucun asset requis,
 * fonctionne offline, taille zéro. Volume par défaut : 0.06 (très bas).
 *
 * L'API AudioContext exige souvent un geste utilisateur pour démarrer ;
 * on tolère l'échec silencieusement (rien de bloquant).
 */
const STORAGE_KEY = "avi-sound-enabled";

let _ctx = null;
function _getCtx() {
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { _ctx = new AC(); } catch { _ctx = null; }
  return _ctx;
}

export function isChimeEnabled() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    // Par défaut activé — l'utilisateur peut désactiver, la valeur
    // "0" persiste ; toute autre valeur ou absente = activé.
    return v !== "0";
  } catch { return true; }
}

export function setChimeEnabled(enabled) {
  try { localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0"); } catch {}
}

export function playAviChime({ volume = 0.06, force = false } = {}) {
  if (!force && !isChimeEnabled()) return;
  const ctx = _getCtx();
  if (!ctx) return;
  // Certains navigateurs suspendent l'AudioContext jusqu'à un geste
  // utilisateur — on tente resume() en no-op si déjà running.
  try { if (ctx.state === "suspended") ctx.resume(); } catch {}
  const now = ctx.currentTime;
  const freqs = [1046.5, 1318.5]; // C6 + E6
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);
  // Enveloppe : 15 ms fade-in → 220 ms fade-out, total ≈ 240 ms.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(volume, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
  freqs.forEach((f) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.26);
  });
}

/** Chime « discussion » — encore plus discret que `playAviChime`.
 *  Sinusoïde unique en G6 (1568 Hz), 110 ms total, volume 0.03 : à peine
 *  perceptible sans être invasif. Utilisé pour signaler l'arrivée d'un
 *  nouveau message sans se confondre avec le chime « décision ». */
export function playAviMessageChime({ volume = 0.03, force = false } = {}) {
  if (!force && !isChimeEnabled()) return;
  const ctx = _getCtx();
  if (!ctx) return;
  try { if (ctx.state === "suspended") ctx.resume(); } catch {}
  const now = ctx.currentTime;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(volume, now + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.11);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.value = 1568; // G6 — plus aigu, très court
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + 0.13);
}
